import { motion } from "motion/react";
import { CheckCircle2, XCircle, HelpCircle, Phone, TrendingUp, Award } from "lucide-react";
import { HalfCircle } from "@/components/ui/HalfCircle";
import { ExperienceButton } from "./ExperienceButton";
import { useState } from "react";

type Score = "yes" | "partially" | "no" | "insufficient";

interface CriterionScore {
  title: string;
  subtitle?: string;
  description?: string;
  subdescription?: string;
  score: Score;
  icon: React.ReactNode;
  accentColor?: string;
}

interface FinalSummaryProps {
  criteria: CriterionScore[];
  vendorName: string;
  recommendation: string;
  onRestart?: () => void;
  onExit?: () => void;
  onPrepareCall?: () => void;
}

export function FinalSummary({ criteria, vendorName, recommendation, onRestart, onExit, onPrepareCall }: FinalSummaryProps) {
  const [showDetails, setShowDetails] = useState(true);

  const scoreConfig = {
    yes: { icon: CheckCircle2, color: "text-emerald-400", bgColor: "bg-emerald-500/15", count: 0 },
    partially: { icon: ({ className }: { className?: string }) => <HalfCircle className={className} />, color: "text-amber-400", bgColor: "bg-amber-500/15", count: 0 },
    no: { icon: XCircle, color: "text-rose-400", bgColor: "bg-rose-500/15", count: 0 },
    insufficient: { icon: HelpCircle, color: "text-slate-400", bgColor: "bg-slate-500/15", count: 0 }
  };

  criteria.forEach(c => {
    scoreConfig[c.score].count++;
  });

  return (
    <div className="min-h-screen bg-[#063F6B] text-white py-16 px-6 relative overflow-hidden">
      {/* Subtle radial glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-[#124C77]/40 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          className="text-center mb-14"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-[#EAA059] to-[#d4893e] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#EAA059]/20">
            <Award className="w-10 h-10 text-white" />
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4 leading-tight tracking-tight">
            Evaluation Complete
          </h1>

          <p className="text-lg text-slate-300/90">
            Your comprehensive analysis of{" "}
            <span className="text-[#EAA059] font-semibold">{vendorName}</span>
          </p>
        </motion.div>

        {/* Score Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          className="rounded-2xl p-6 sm:p-8 lg:p-12 border border-white/[0.08] bg-white/[0.04] backdrop-blur-sm mb-8"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            {Object.entries(scoreConfig).map(([key, config], index) => {
              const Icon = config.icon;
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                  className="text-center"
                >
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl ${config.bgColor} mb-3`}>
                    <Icon className={`w-7 h-7 ${config.color}`} />
                  </div>
                  <div className="text-2xl font-semibold text-white mb-1">{config.count}</div>
                  <div className="text-sm text-slate-400 capitalize">{key}</div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="border-t border-white/[0.08] pt-8"
          >
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-[#EAA059]" />
              Strategic Recommendation
            </h3>
            <p className="text-slate-300/90 leading-relaxed">
              {recommendation}
            </p>
          </motion.div>
        </motion.div>

        {/* Criteria Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
        >
          <ExperienceButton
            onClick={() => setShowDetails(!showDetails)}
            className="w-full mb-6 py-4 text-base bg-white/[0.04] border border-white/[0.08] text-white hover:bg-white/[0.07] hover:border-[#EAA059]/25 transition-all duration-300 rounded-xl"
          >
            {showDetails ? "Hide" : "Show"} Detailed Breakdown
          </ExperienceButton>

          {showDetails && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8"
            >
              {criteria.map((criterion, index) => {
                const config = scoreConfig[criterion.score];
                const Icon = config.icon;
                const scoreLabel = { yes: "Met", partially: "Partially Met", no: "Not Met", insufficient: "Insufficient Info" }[criterion.score];
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.06, duration: 0.5 }}
                    className="rounded-2xl p-5 border border-white/[0.08] bg-white/[0.04] hover:bg-white/[0.06] transition-colors duration-300 flex flex-col gap-2"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: criterion.accentColor || "#94a3b8" }}
                      >
                        <div className="text-white [&>svg]:w-[18px] [&>svg]:h-[18px]">{criterion.icon}</div>
                      </div>
                      <p className="font-semibold text-white text-sm flex-1 min-w-0">
                        {criterion.title} &ndash; {criterion.subtitle}
                      </p>
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full flex-shrink-0 ${config.bgColor}`}>
                        <Icon className={`w-3.5 h-3.5 ${config.color}`} />
                        <span className={`text-xs font-semibold whitespace-nowrap ${config.color}`}>{scoreLabel}</span>
                      </div>
                    </div>
                    {criterion.description && (
                      <p className="text-sm font-medium text-white/70 italic">{criterion.description}</p>
                    )}
                    {criterion.subdescription && (
                      <p className="text-xs text-slate-400 leading-relaxed">{criterion.subdescription}</p>
                    )}
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="flex flex-wrap gap-4 justify-center"
        >
          {onPrepareCall && (
            <ExperienceButton
              onClick={onPrepareCall}
              className="px-8 py-4 bg-gradient-to-r from-[#D4893E] to-[#C07830] hover:from-[#C07830] hover:to-[#B06A25] text-white text-lg font-medium rounded-full transition-all duration-300 shadow-lg shadow-[#D4893E]/20 hover:shadow-xl hover:shadow-[#D4893E]/30"
            >
              <Phone className="mr-2 w-5 h-5" />
              Start a Vendor Call
            </ExperienceButton>
          )}
          {onRestart && (
            <ExperienceButton
              onClick={onRestart}
              className="px-8 py-4 bg-white/[0.05] border border-white/[0.08] text-white text-lg font-medium rounded-full hover:bg-white/[0.08] hover:border-[#EAA059]/25 transition-all duration-300"
            >
              Start New Evaluation
            </ExperienceButton>
          )}
          {onExit && (
            <ExperienceButton
              onClick={onExit}
              className="px-8 py-4 bg-white/[0.05] border border-white/[0.08] text-white text-lg font-medium rounded-full hover:bg-white/[0.08] hover:border-[#EAA059]/25 transition-all duration-300"
            >
              Back to Reports
            </ExperienceButton>
          )}
        </motion.div>
      </div>
    </div>
  );
}
